import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Order } from 'src/services/classes/models/order.interface';
import { BluetoothPrinterService } from 'src/services/data/bluetooth-printer/bluetooth-printer.service';
import { NotificationsService } from 'src/services/classes/notifications/notifications.service';
import { OrdersService } from 'src/services/data/orders/orders.service';
import { Response } from 'src/services/classes/models/response.interface';
import { Logger } from 'src/services/core/logger/logger.service';

const log = new Logger('Order-Card');

@Component({
  selector: 'app-order-card',
  templateUrl: './order-card.component.html',
  styleUrls: ['./order-card.component.scss'],
})
export class OrderCardComponent implements OnInit, OnDestroy {
  @Input() order!: Order;
  @Output() handleOrderUpdate = new EventEmitter<Order>();

  countdownText: string = '';
  showCountdown: boolean = false;
  private countdownInterval: any;

  constructor(
    private router: Router,
    private bluetoothPrinterService: BluetoothPrinterService,
    private notifyService: NotificationsService,
    private ordersService: OrdersService
  ) {}

  ngOnInit() {
    if (this.order.inTransit && this.order.reservationEndTime) {
      this.startCountdownCheck();
    }
  }

  ngOnDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  private startCountdownCheck() {
    this.countdownInterval = setInterval(() => {
      this.updateCountdown();
    }, 1000); // Update every second
    
    // Initial check
    this.updateCountdown();
  }

  private updateCountdown() {
    const now = new Date();
    const endTime = new Date(this.order.reservationEndTime);
    const timeDiff = endTime.getTime() - now.getTime();
    
    // Check if within 10 minutes (600,000 milliseconds)
    if (timeDiff <= 1200000 && timeDiff > 0) {
      this.showCountdown = true;
      const minutes = Math.floor(timeDiff / 60000);
      const seconds = Math.floor((timeDiff % 60000) / 1000);
      this.countdownText = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    } else if (timeDiff <= 0) {
      this.showCountdown = true;
      this.countdownText = 'EXPIRED';
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
    } else {
      this.showCountdown = false;
    }
  }

  getObjectLength(dishes: any) {
    return Object.keys(dishes).length;
  }

  isImageValid(control: string) {
    return /^(ftp|https|http)?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/.test(
      control
    );
  }

  goToDetails(orderId: number) {
    this.router.navigateByUrl(`/live-orders/${orderId}`);
  }

  printOrder(event: Event) {
    event.stopPropagation();
    // Fetch full order details for printing
    this.ordersService.getOrderById(this.order.id).subscribe({
      next: (res: Response) => {
        const orderDetails = res.status.toLowerCase() === 'success' ? res.data : this.order;
        this.printViaBluetooth(orderDetails);
      },
      error: (error: any) => {
        log.debug('Error fetching order details: ', error);
        this.printViaBluetooth(this.order);
      }
    });
  }

  async printViaBluetooth(order: any) {
    if (this.bluetoothPrinterService.isAvailable()) {
      try {
        await this.bluetoothPrinterService.printReceipt(order);
        this.notifyService.publishMessages('Receipt sent to printer successfully', 'success', 2);
      } catch (error: any) {
        log.debug('Bluetooth print failed, falling back to browser print:', error);
        this.notifyService.publishMessages(
          'Bluetooth printing failed. Using browser print instead.',
          'warning',
          2
        );
        this.generatePrintContent(order);
      }
    } else {
      this.notifyService.publishMessages(
        'Bluetooth not available. Using browser print.',
        'info',
        2
      );
      this.generatePrintContent(order);
    }
  }

  generatePrintContent(order: any) {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const createdDate = new Date(order.createdTime);
    const formattedCreatedDate = createdDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    let dishesHtml = '';
    if (order.dishes) {
      Object.entries(order.dishes).forEach(([category, items]: [string, any]) => {
        if (Array.isArray(items)) {
          items.forEach((item: any) => {
            dishesHtml += `
              <tr>
                <td>${item.name || 'N/A'}</td>
                <td>${item.quantity || 1}</td>
                <td>₦${(item.unitPrice || 0).toFixed(2)}</td>
                <td>₦${(item.totalPrice || 0).toFixed(2)}</td>
              </tr>
            `;
            if (item.addons && item.addons.length > 0) {
              item.addons.forEach((addon: any) => {
                dishesHtml += `
                  <tr style="background-color: #f5f5f5;">
                    <td style="padding-left: 30px;">+ ${addon.name}</td>
                    <td>${addon.quantity || 1}</td>
                    <td>₦${(addon.price || 0).toFixed(2)}</td>
                    <td>₦${((addon.price || 0) * (addon.quantity || 1)).toFixed(2)}</td>
                  </tr>
                `;
              });
            }
          });
        }
      });
    }

    const waiterName = order.assignedUsername || order.waiter?.name || 'N/A';
    const kitchenStaff = order.assignedKBStaff || 'N/A';

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order - ${order.orderId || order.code}</title>
          <style>
            @media print {
              @page { margin: 20mm; }
              body { margin: 0; padding: 0; }
              .no-print { display: none; }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
              margin-bottom: 20px;
            }
            .header h1 { margin: 0; font-size: 24px; }
            .status-badge {
              display: inline-block;
              padding: 5px 15px;
              border-radius: 20px;
              font-weight: bold;
              margin-top: 10px;
            }
            .status-new { background-color: #fef3c7; color: #d97706; }
            .status-accepted { background-color: #dbeafe; color: #2563eb; }
            .status-ready { background-color: #d1fae5; color: #059669; }
            .order-info { margin-bottom: 20px; }
            .order-info table { width: 100%; border-collapse: collapse; }
            .order-info td {
              padding: 8px;
              border-bottom: 1px solid #ddd;
            }
            .order-info td:first-child { font-weight: bold; width: 40%; }
            .dishes-table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            .dishes-table th {
              background-color: #6941c6;
              color: white;
              padding: 12px;
              text-align: left;
            }
            .dishes-table td {
              padding: 10px;
              border-bottom: 1px solid #ddd;
            }
            .totals { margin-top: 20px; text-align: right; }
            .totals table { width: 100%; border-collapse: collapse; }
            .totals td { padding: 8px; }
            .totals td:first-child { text-align: left; font-weight: bold; }
            .totals .total-row {
              font-size: 18px;
              font-weight: bold;
              border-top: 2px solid #333;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
            .print-button { margin: 20px 0; text-align: center; }
            .print-button button {
              background-color: #6941c6;
              color: white;
              border: none;
              padding: 10px 20px;
              font-size: 16px;
              cursor: pointer;
              border-radius: 4px;
            }
          </style>
        </head>
        <body>
          <div class="order-info">
            <table>
              <tr>
                <td>Order ID:</td>
                <td>${order.orderId || order.code || 'N/A'}</td>
              </tr>
              <tr>
                <td>Table:</td>
                <td>${order.table || 'N/A'}</td>
              </tr>
              <tr>
                <td>Order Type:</td>
                <td>${order.orderType || 'N/A'}</td>
              </tr>
              <tr>
                <td>Date & Time:</td>
                <td>${formattedCreatedDate}</td>
              </tr>
              <tr>
                <td>Kitchen Staff:</td>
                <td>${kitchenStaff}</td>
              </tr>
              <tr>
                <td>Waiter:</td>
                <td>${waiterName}</td>
              </tr>
              ${order.specialRequest ? `
              <tr>
                <td>Special Request:</td>
                <td style="color: #d97706; font-weight: bold;">${order.specialRequest}</td>
              </tr>
              ` : ''}
            </table>
          </div>

          <table class="dishes-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${dishesHtml || '<tr><td colspan="4" style="text-align: center;">No items found</td></tr>'}
            </tbody>
          </table>

          <div class="totals">
            <table>
              <tr>
                <td>Subtotal:</td>
                <td>₦${(order.subTotal || 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td>Tax:</td>
                <td>₦${(order.tax || 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td>Service Charge:</td>
                <td>₦${(order.serviceCharge || 0).toFixed(2)}</td>
              </tr>
              ${order.takeawayPriceTotal ? `
              <tr>
                <td>Takeaway:</td>
                <td>₦${(order.takeawayPriceTotal || 0).toFixed(2)}</td>
              </tr>
              ` : ''}
              <tr class="total-row">
                <td>Total Amount:</td>
                <td>₦${(order.totalAmount || 0).toFixed(2)}</td>
              </tr>
            </table>
          </div>

          <div class="footer">
            <p>Generated on ${new Date().toLocaleString()}</p>
          </div>

          <div class="print-button no-print">
            <button onclick="window.print()">Print Order</button>
          </div>

          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  }
}
